import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Alert, Badge, Button, Col, Container, FormGroup, Row, Spinner } from 'reactstrap';
import { DiscoverService, VocabularyService } from '../../../services';
import useDebounce from '../../../utils/useDebounce';

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const CATEGORIES = [
  { value: '', label: 'All Topics' },
  { value: 'daily_life', label: 'Daily Life' },
  { value: 'work_business', label: 'Work & Business' },
  { value: 'school_academic', label: 'School & Academic' },
  { value: 'travel', label: 'Travel' },
  { value: 'technology', label: 'Technology' },
  { value: 'health', label: 'Health' },
  { value: 'food', label: 'Food' },
  { value: 'culture', label: 'Culture' },
  { value: 'emotion', label: 'Emotion' },
  { value: 'action_verb', label: 'Action Verbs' },
  { value: 'slang_idiom', label: 'Slang & Idiom' },
  { value: 'sports_fitness', label: 'Sports & Fitness' },
];

const cefrColor = { A1: 'success', A2: 'success', B1: 'primary', B2: 'primary', C1: 'warning', C2: 'danger' };

const playAudio = (url) => {
  new Audio(url).play().catch(() => {});
};

const WordCard =({ entry, onSave, saved }) => {
  const sense = entry.senses?.[0];
  return (
    <div
      className="bg-white rounded shadow-sm p-3 h-100"
      style={{ border: '1px solid #f1f3f5' }}
    >
      <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
        <div>
          <span className="fw-bold text-dark" style={{ fontSize: '1.05rem' }}>
            {entry.display_word}
          </span>
          {entry.audio_url && (
            <button
              className="btn btn-link p-0 text-muted ms-2"
              style={{ fontSize: '1rem', lineHeight: 1 }}
              onClick={() => playAudio(entry.audio_url)}
              title="Listen"
            >
              <i className="uil uil-volume-up" />
            </button>
          )}
          {entry.pronunciation && (
            <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
              {entry.pronunciation}
            </span>
          )}
        </div>
        <div className="d-flex gap-1 flex-shrink-0">
          {entry.cefr_level && (
            <Badge color={cefrColor[entry.cefr_level] || 'secondary'} pill>
              {entry.cefr_level}
            </Badge>
          )}
          {entry.part_of_speech && (
            <Badge color="light" pill className="text-muted border">
              {entry.part_of_speech}
            </Badge>
          )}
        </div>
      </div>
      {sense?.definition && (
        <p className="mb-1 text-dark" style={{ fontSize: '0.9rem' }}>
          {sense.definition}
        </p>
      )}
      {sense?.example && (
        <p className="mb-2 text-muted fst-italic" style={{ fontSize: '0.85rem' }}>
          "{sense.example}"
        </p>
      )}
      <Button
        size="sm"
        color={saved ? 'success' : 'outline-primary'}
        className="mt-auto"
        onClick={() => onSave(entry)}
        disabled={saved}
      >
        {saved ? '✓ Saved' : '+ Save to My Words'}
      </Button>
    </div>
  );
};

const PageDiscover = () => {
  const [query, setQuery] = useState('');
  const [cefrLevel, setCefrLevel] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [saved, setSaved] = useState(new Set());

  const debouncedQuery = useDebounce(query.trim(), 500);
  const isSearching = debouncedQuery.length >= 2;

  const searchResult = useQuery(
    ['discoverSearch', debouncedQuery, page],
    () => DiscoverService.searchWords({ query: debouncedQuery, page, size: 20 }),
    { enabled: isSearching, retry: false },
  );

  const browseResult = useQuery(
    ['discoverBrowse', cefrLevel, category, page],
    () => DiscoverService.browseWords({ cefr_level: cefrLevel || undefined, category: category || undefined, sort: 'random', page, size: 20 }),
    { enabled: !isSearching, retry: false },
  );

  const { data, isLoading } = isSearching ? searchResult : browseResult;
  const entries = data?.data || data || [];

  const [saveError, setSaveError] = useState(null);

  const { mutate: saveWord } = useMutation(VocabularyService.saveVocabulary, {
    onSuccess: (_, vars) => {
      setSaveError(null);
      setSaved((prev) => new Set([...prev, vars.word]));
    },
    onError: (err, vars) => {
      // Already saved counts as saved
      if (err?.response?.status === 409) {
        setSaved((prev) => new Set([...prev, vars.word]));
      }
      setSaveError(VocabularyService.getErrorMessage(err));
    },
  });

  const handleSave = (entry) => {
    saveWord({ word: entry.lemma || entry.display_word });
  };

  const handleCefr = (level) => {
    setCefrLevel(level === cefrLevel ? '' : level);
    setPage(1);
  };

  return (
    <section className="section p-0 mt-4">
      <Container>
        {/* Search */}
        <Row className="mb-3">
          <Col md={6}>
            <FormGroup className="mb-0">
              <input
                type="text"
                className="form-control rounded-pill shadow-sm"
                placeholder="Search for any English word..."
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* CEFR + Topic filter (browse mode only) */}
        {!isSearching && (
          <div className="d-flex gap-2 flex-wrap align-items-center mb-4">
            {CEFR_LEVELS.map((level) => (
              <Button
                key={level}
                size="sm"
                color={cefrLevel === level ? 'primary' : 'outline-secondary'}
                className="rounded-pill"
                onClick={() => handleCefr(level)}
              >
                {level}
              </Button>
            ))}
            <select
              className="form-select form-select-sm rounded-pill ms-2"
              style={{ maxWidth: 180 }}
              value={category}
              onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            {(cefrLevel || category) && (
              <Button size="sm" color="link" className="text-muted p-0 ms-1" onClick={() => { setCefrLevel(''); setCategory(''); setPage(1); }}>
                Clear filters
              </Button>
            )}
          </div>
        )}

        {saveError && (
          <Alert color="warning" toggle={() => setSaveError(null)}>{saveError}</Alert>
        )}

        {/* Results */}
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2">Loading words...</p>
          </div>
        ) : entries.length === 0 ? (
          <Alert color="info">
            {isSearching ? `No results found for "${debouncedQuery}".` : 'No words found. Try a different filter.'}
          </Alert>
        ) : (
          <>
            <Row className="g-3">
              {entries.map((entry) => (
                <Col key={entry.id} lg={4} md={6} sm={12}>
                  <WordCard
                    entry={entry}
                    onSave={handleSave}
                    saved={saved.has(entry.lemma || entry.display_word)}
                  />
                </Col>
              ))}
            </Row>
            <div className="d-flex justify-content-center gap-2 mt-4 mb-5">
              <Button size="sm" color="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <span className="align-self-center text-muted small">Page {page}</span>
              <Button size="sm" color="outline-secondary" disabled={entries.length < 20} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </>
        )}
      </Container>
    </section>
  );
};

export default PageDiscover;
